import { useTranslation } from 'next-i18next'
import { type ReactNode } from 'react'

import { TRANSLATION_KEYS } from '@/constants/localization'
import { RichCardImageOverlay } from '@/features/listing/components/rich-card/rich-card-media/RichCardImageOverlay'
import {
  RichCardImage,
  RichCardImageLinkWrapper,
  RichCardImagePanelContainer,
} from '@/features/listing/components/rich-card/styled'
import { type GenericListing } from '@/features/listing/types/listings'

type RichCardImagePanelProps = {
  imageUrl: string | null
  index: number
  listing: GenericListing
  showDetailsOverlay?: boolean
  titleOverride?: string | ReactNode
}

export const RichCardImagePanel = ({
  index,
  imageUrl,
  listing,
  showDetailsOverlay,
  titleOverride,
}: RichCardImagePanelProps) => {
  /**
   * we only show the original title in the overlay if we've
   * overriden it in the card heading. we don't need to show it twice.
   */
  const { title, url, imageUrls } = listing
  const description = 'description' in listing ? listing.description : undefined
  const imageCount = 'imageCount' in listing ? listing.imageCount : undefined
  const { t } = useTranslation([TRANSLATION_KEYS.LISTING])
  const listingHasMoreImages = (imageCount ?? imageUrls.length - 1) > index

  // Only show view more badge if we are showing the overlay slide, and we know the listing has more images to show on VIP
  const showViewMore = showDetailsOverlay && listingHasMoreImages

  return (
    <RichCardImagePanelContainer data-testid="rich-card-image-panel">
      <RichCardImageLinkWrapper noStyle href={url}>
        {showDetailsOverlay && (
          <RichCardImageOverlay
            title={titleOverride && title}
            description={description}
            showViewMore={showViewMore}
          />
        )}
        {imageUrl == 'blank' ? null : (
          <RichCardImage
            alt={`${title}, ${t('listing:rich_card.view_more')}`}
            data-testid="rich-card-image"
            src={imageUrl}
            width={400}
            extension={'WEBP'}
            loading={index < 2 ? 'eager' : 'lazy'}
            skipNextImage
          />
        )}
      </RichCardImageLinkWrapper>
    </RichCardImagePanelContainer>
  )
}
